import React, { useState, useEffect, useRef } from 'react'
import Curve from '@components/app/curve'
import AboutEditor from '@components/app/editor/about'
import CoverartEditor from '@components/app/editor/coverart'
import PublicitySetting from '@components/app/editor/publicity'
import Sortable from '@components/app/editor/sortable'
import GenreList from '@components/app/genre-dropdown'
// import Avatar from '@components/app/avatar'
import Waveform from '@components/app/waveform'
import { Button, ToggleButton } from '@components/common/button'
import BtnPlay from '@components/common/button/play'
import Spinner from '@components/common/spinner'
import { updateAlbum, deleteAlbum } from '@data/api/album'
import MessagesCtx from '@data/contexts/messages'

import { getFilename } from '@utils/cdn-image'
import classnames from 'classnames'
import debounce from 'lodash/debounce'
import Router from 'next/router'
import { FormattedMessage, useIntl, FormattedDate } from 'react-intl'
import CdnImage from '@/components/app/cdn-image'
import { AlbumProps } from '../../../../types/album'
import editStyles from '../edit.module.scss'
import styles from '../media.module.scss'
import messages from '../messages'

interface Props {
  defaultAlbum: AlbumProps
  toggleEdit: () => void
  onUpdate: (album: AlbumProps) => void
  // addMessage: (msg: string) => void
}

const Album = ({ defaultAlbum, toggleEdit, onUpdate }: Props) => {
  const intl = useIntl()
  const [ isActive, setActive ] = useState(false)
  const [ isLoading, setLoading ] = useState(false)
  const [ album, setAlbum ] = useState(defaultAlbum)
  const [ isDeleting, setDeleting ] = useState(false)
  const [ priceError, setPriceError ] = useState('')
  const albumRef = useRef(album)

  // eslint-disable-next-line max-len
  const [ coverart, setCoverart ] = useState(
    album.coverart_crop_url ? getFilename(album.coverart_crop_url) : '',
  )

  const { addMessage } = MessagesCtx.useContainer()

  // const currentSound = album.sounds[currentIndex]
  const sounds = {}
  defaultAlbum.sounds.forEach((s) => {
    sounds[s.id] = s
  })
  const soundIds = defaultAlbum.sounds.map((s) => s.id)
  const [ orders, setOrders ] = useState(soundIds)

  const onChange = (params) => {
    albumRef.current = { ...albumRef.current, ...params }
    setAlbum(albumRef.current)
  }
  // eslint-disable-next-line max-len
  const onAboutChange = debounce((descTxt, hashtags) =>
    onChange({ description: descTxt, tags: hashtags }),
  )

  const onCancel = () => {
    setActive(false)
    setTimeout(() => toggleEdit(), 200)
  }

  const onSubmit = async () => {
    if (album.onSale) {
      if (+album.price !== 0 && +album.price < 50) {
        setPriceError(
          intl.formatMessage(
            {
              id: 'soundnew.media.price.error',
              defaultMessage: 'Price should be ¥50 or more.',
            },
          ),
        )
        return
      }
      if (!Number.isInteger(+album.price)) {
        setPriceError(
          intl.formatMessage(
            {
              id: 'buy.priceIntError',
              defaultMessage: 'Please input integer',
            },
          ),
        )
        return
      }
    }
    setPriceError('')
    const updates = {
      id: defaultAlbum.id,
      title: album.title,
      description: album.description,
      tags: album.tags,
      coverart_url: coverart,
      _sounds: orders,
      status: album.status,
      sales: {
        on: album.onSale,
        price: album.price,
        nameyourprice: album.nameyourprice,
      },
      genre: album.genre,
    }

    setLoading(true)
    const result = await updateAlbum(updates)
    if (result.error) {
      setLoading(false)
      // TODO: show message
      addMessage(intl.formatMessage(messages.albumSaveError))
      return
    }
    setActive(false)
    setTimeout(() => {
      setLoading(false)
      onUpdate(result)
      addMessage(intl.formatMessage(messages.albumSaved))
    }, 200)
  }

  const onDelete = async (id) => {
    const result = await deleteAlbum(id)
    if (!result || result.error) {
      return
    }
    setDeleting(false)
    Router.back()
  }

  useEffect(() => setActive(true), [ defaultAlbum ])

  return (
    <div className={styles.mediaContainer}>
      <div
        className={classnames(editStyles.navbar, {
          [editStyles.active]: isActive,
        })}
      >
        <div className="container-md d-flex align-items-center">
          <h1 className="flex1 tx-18 tx-g100 tx-ellipsis m-0 pr-3">
            Edit: [ {album.title} ]
          </h1>
          <div className="flex0">
            {!isLoading && (
              <button
                className={`${editStyles.cancel} btn-chromeless mr-3`}
                type="button"
                onClick={onCancel}
              >
                <FormattedMessage
                  id="sound.edit.btn.cancel"
                  defaultMessage="Cancel"
                />
              </button>
            )}
            <Button
              className="primary-alt"
              onClick={onSubmit}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Spinner size={14} className="mr-3" inverse />
                  <FormattedMessage
                    id="sound.edit.btn.saving"
                    defaultMessage="Saving..."
                  />
                </>
              ) : (
                <FormattedMessage
                  id="album.edit.btn.save"
                  defaultMessage="Save this Album"
                />
              )}
            </Button>
          </div>
        </div>
      </div>
      {isLoading && <div className={editStyles.loading} />}
      <Curve className={styles.coverartBlurContainer}>
        <CdnImage
          defaultSrc="data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8+vHXfwAJpAPhLJ1EpAAAAABJRU5ErkJggg=="
          src={coverart}
          blur={20}
          alt=""
          className={styles.coverartBlurImage}
          width={500}
          height={500}
        />
      </Curve>
      <div className="container-md">
        <div className={`d-flex ${styles.header} ${editStyles.header}`}>
          <div className={`${styles.coverartContainer}`}>
            <div className="ps-relative d-inline-block m-auto">
              <figure className={`shadow-lg ${styles.coverart}`}>
                <CoverartEditor
                  src={coverart}
                  onCompleted={(f) => setCoverart(f)}
                />
              </figure>
            </div>
          </div>
          <div className={`${styles.heading} mb-5`}>
            <div className={styles.headingText}>
              <div className="d-flex align-items-center mb-4">
                <div className="flex0 mr-3">
                  <BtnPlay className={styles.headingPlayBtn} disabled />
                </div>
                <div className="flex1">
                  <div className="tx-24 mb-1">
                    <input
                      type="text"
                      className={`${styles.title} ${editStyles.title} ${editStyles.input} tx-w700`}
                      value={album.title}
                      onChange={(e) => onChange({ title: e.target.value })}
                      placeholder={intl.formatMessage(
                        messages.albumTitlePlaceholder,
                      )}
                      autoComplete="off"
                      required
                    />
                  </div>
                  {album.released_at ? (
                    <span className={`${styles.released} tx-w300 tx-14`}>
                      <span>Released</span>
                      <span className="dot" />
                      <time>
                        <FormattedDate value={album.released_at} />
                      </time>
                    </span>
                  ) : (
                    <span className={`${styles.released} tx-w300 tx-14`}>
                      Not released yet.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-4 order-md-2 mb-5">
            <div className="sales">
              <div className="d-flex align-items-center mb-4">
                <div className="flex1">
                  <FormattedMessage
                    id="soundnew.media.sales.title"
                    defaultMessage="On Sale"
                    tagName="h4"
                  />
                  <p className="flex1 help-block mb-md-0">
                    <FormattedMessage {...messages.mediaSellAlbum} />
                  </p>
                </div>
                <div className="flex0">
                  <ToggleButton
                    on={album.onSale}
                    onClick={(on) => onChange({ onSale: on })}
                  />
                </div>
              </div>
              {album.onSale && (
                <>
                  <label htmlFor="price">
                    <span className="mr-3">
                      <FormattedMessage {...messages.mediaPriceLabel} />
                    </span>
                    ¥
                    <input
                      id="price"
                      type="number"
                      name="price"
                      step="100"
                      // eslint-disable-next-line max-len
                      className={`${editStyles.input} ${editStyles.inline} ${editStyles.price} tx-32 tx-w600`}
                      value={album.price}
                      disabled={!album.onSale}
                      onChange={(e) => onChange({ price: e.target.value })}
                    />
                    <span
                      className={classnames(
                        'tx-14',
                        { 'tx-g600': !album.nameyourprice },
                        { 'tx-linethrough': !album.nameyourprice },
                      )}
                    >
                      <FormattedMessage {...messages.mediaPriceMore} />
                    </span>
                  </label>
                  {
                    !!priceError && (
                      <span className="d-inline-block tx-warning tx-14">
                        {priceError}
                      </span>
                    )
                  }
                  <div className={classnames('d-flex align-items-center mb-2 mt-4')}>
                    <div className="flex1 pr-2">
                      <p className="mb-1">
                        <FormattedMessage
                          id="soundnew.media.nameyourprice.label"
                          defaultMessage="Name your price"
                        />
                      </p>
                      <p className="help-block">
                        <FormattedMessage
                          id="soundnew.media.nameyourprice.text"
                          defaultMessage="User can pay more than the price."
                        />
                      </p>
                    </div>
                    <div className="flex0">
                      <ToggleButton
                        on={album.nameyourprice}
                        onClick={(on) => onChange({ nameyourprice: on })}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <PublicitySetting
              id={album.id}
              publicity={album.status}
              onUpdate={(e) => onChange({ status: e })}
            />
          </div>{' '}
          {/* end right column */}
          <div className="col-md-8">
            <h4>
              <FormattedMessage id="app.genre" defaultMessage="Genre" />
            </h4>
            <div className="mb-5">
              <GenreList
                value={album.genre}
                onChange={(val) => onChange({ genre: val })}
              />
            </div>
            <h4>
              <FormattedMessage {...messages.albumDescriptionLabel} />
            </h4>
            <div className="tx-14 mb-5">
              <AboutEditor about={album.description} onChange={onAboutChange} />
            </div>
            <h2 className="heading">
              <span className="text">Album Tracks</span>
            </h2>
            <div className={`${editStyles.tracks} mb-5`}>
              <Sortable ids={soundIds} onUpdate={setOrders} isSortable>
                {({ index, id, dragging, dragHandleProps }) => (
                  <div
                    className={classnames('d-flex p-3', editStyles.media, {
                      [editStyles.dragging]: dragging,
                    })}
                  >
                    <div className="flex0 mr-3">
                      <div className={editStyles.handleContainer}>
                        <span
                          // eslint-disable-next-line max-len
                          className={`tx-center tx-oswald tx-16 tx-w600 tx-g400 ${styles.index} ${editStyles.text}`}
                        >
                          {index + 1}.
                        </span>
                        <span
                          // eslint-disable-next-line max-len
                          className={`tx-center tx-16 tx-g800 ${editStyles.handle} solicon-split-v`}
                          {...dragHandleProps}
                        />
                      </div>
                    </div>
                    <div className="flex1">
                      <header className="d-flex align-items-center mb-3">
                        <h3 className="tx-20">{sounds[id].title}</h3>
                      </header>
                      {sounds[id].description && (
                        <p className="tx-14 mb-2">
                          {sounds[id].description.substr(0, 50)}
                          {sounds[id].description.length > 50 && '...'}
                        </p>
                      )}
                      <Waveform
                        src={sounds[id].waveform_url}
                        className={`mb-2 ${styles.waveform}`}
                        duration={sounds[id].metadata.duration}
                        height={30}
                        static
                      />
                    </div>
                  </div>
                )}
              </Sortable>
            </div>
          </div>{' '}
          {/* end left column */}
        </div>
        <div className="hr" />
        <div className="tx-center p-5">
          {isDeleting ? (
            <>
              <p>Are you sure you want to delete?</p>
              <div className="tx-center">
                <button
                  type="button"
                  className="btn-chromeless mr-3"
                  onClick={() => setDeleting(false)}
                >
                  <FormattedMessage
                    id="sound.edit.cancel"
                    defaultMessage="Cancel"
                  />
                </button>
                <Button className="primary" onClick={() => onDelete(album.id)}>
                  <FormattedMessage
                    id="album.edit.delete"
                    defaultMessage="Delete this Album"
                  />
                </Button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="btn-chromeless tx-primary"
              onClick={() => setDeleting(true)}
            >
              <FormattedMessage
                id="album.edit.delete"
                defaultMessage="Delete this Album"
              />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default Album
